/**
 * WarehousesTab — manage laundry depots (start/end points for delivery routes).
 *
 * Lives inside SettingsTab. Lets manager+ create / edit / delete / archive
 * warehouses and pick a primary one (becomes the default start+end in
 * route planning).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Warehouse as WarehouseIcon, Plus, Star, Edit2, Trash2, MapPin, X, Check } from "lucide-react";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const blankForm = () => ({ name: "", address: "", lat: null, lng: null, place_id: "", notes: "" });

export default function WarehousesTab() {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = creating
  const [form, setForm] = useState(blankForm());

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/warehouses?include_archived=true`, { headers: getAuthHeader() });
      setWarehouses(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load warehouses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(blankForm());
    setDialogOpen(true);
  };

  const openEdit = (w) => {
    setEditing(w);
    setForm({
      name: w.name || "",
      address: w.address || "",
      lat: w.lat,
      lng: w.lng,
      place_id: w.place_id || "",
      notes: w.notes || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Warehouse name is required");
      return;
    }
    try {
      const payload = {
        name: form.name.trim(),
        address: (form.address || "").trim(),
        lat: form.lat ?? null,
        lng: form.lng ?? null,
        place_id: form.place_id || null,
        notes: form.notes || null,
      };
      if (editing) {
        await axios.patch(`${API}/warehouses/${editing.id}`, payload, { headers: getAuthHeader() });
        toast.success("Warehouse updated");
      } else {
        await axios.post(`${API}/warehouses`, payload, { headers: getAuthHeader() });
        toast.success("Warehouse added");
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const setPrimary = async (w) => {
    try {
      await axios.post(`${API}/warehouses/${w.id}/set-primary`, {}, { headers: getAuthHeader() });
      toast.success(`'${w.name}' is now the primary warehouse`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (w) => {
    if (!window.confirm(`Delete warehouse '${w.name}'? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/warehouses/${w.id}`, { headers: getAuthHeader() });
      toast.success("Warehouse deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const toggleArchive = async (w) => {
    try {
      await axios.patch(`${API}/warehouses/${w.id}`, { is_archived: !w.is_archived }, { headers: getAuthHeader() });
      toast.success(w.is_archived ? "Restored" : "Archived");
      await load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <WarehouseIcon className="h-6 w-6" />
          <div>
            <h2 className="text-2xl font-bold">Warehouses</h2>
            <p className="text-gray-500 text-sm">
              Manage depot locations. The primary one is used as the default start/end of delivery routes.
            </p>
          </div>
        </div>
        <Button onClick={openCreate} data-testid="warehouse-add-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add warehouse
        </Button>
      </div>

      {loading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {!loading && warehouses.length === 0 && (
        <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">
          No warehouses yet. Add your first depot to enable route planning.
        </CardContent></Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {warehouses.map((w) => (
          <Card key={w.id} className={`border-2 ${w.is_primary ? "border-brand-navy" : "border-neutral-200"} ${w.is_archived ? "opacity-60" : ""}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold truncate">{w.name}</span>
                    {w.is_primary && <Badge className="bg-brand-navy/10 text-brand-navy border-brand-navy/30"><Star className="w-3 h-3 mr-1" />Primary</Badge>}
                    {w.is_archived && <Badge variant="outline" className="text-amber-700 border-amber-300">Archived</Badge>}
                    {w.lat != null && w.lng != null && (
                      <Badge variant="outline" className="text-green-700 border-green-300 text-[10px]">
                        <MapPin className="w-3 h-3 mr-0.5" />Geocoded
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{w.address || "(no address)"}</p>
                  {w.notes && <p className="text-xs italic text-muted-foreground mt-1">{w.notes}</p>}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {!w.is_primary && !w.is_archived && (
                    <Button size="icon" variant="outline" onClick={() => setPrimary(w)} title="Set primary" data-testid={`warehouse-set-primary-${w.id}`}>
                      <Star className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button size="icon" variant="outline" onClick={() => openEdit(w)} data-testid={`warehouse-edit-${w.id}`}>
                    <Edit2 className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => toggleArchive(w)}
                    title={w.is_archived ? "Restore" : "Archive"}
                  >
                    {w.is_archived ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                  </Button>
                  <Button size="icon" variant="outline" className="text-red-700 border-red-300 hover:bg-red-50" onClick={() => remove(w)} data-testid={`warehouse-delete-${w.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="warehouse-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit warehouse" : "New warehouse"}</DialogTitle>
            <DialogDescription>
              Pick a real address from the suggestions to enable route optimization.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Sydney Depot"
                data-testid="warehouse-name-input"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Address</Label>
              <AddressAutocomplete
                value={form.address}
                onChange={(v) => setForm({ ...form, address: v })}
                onPlaceSelected={(p) => setForm({ ...form, address: p.address, lat: p.lat, lng: p.lng, place_id: p.place_id })}
                placeholder="Start typing an address…"
                testId="warehouse-address-input"
              />
              {form.lat != null && form.lng != null && (
                <p className="text-xs text-green-700 mt-1">
                  <MapPin className="inline w-3 h-3 mr-0.5" />
                  {form.lat.toFixed(5)}, {form.lng.toFixed(5)}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Notes</Label>
              <Textarea
                value={form.notes || ""}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Loading dock instructions, gate code, etc."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="warehouse-save-btn">{editing ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
